import { useState, useEffect, useMemo } from 'react';
import { Button, Modal, Row, Col, Form } from 'react-bootstrap';

import { useAppSelector, useAppDispatch } from 'redux/store';
import { notify } from 'messages';
import { updateTable, createTable } from '../../../../redux/slices/tablesSlice';
import { getAuthorizedZones } from '../../../../redux/slices/lists/zonesSlice';
import { FolderSelect } from 'components/forms/FolderSelect';
import type { CreateTarget } from 'actions';
import type { FetchedTable } from 'api/tables/endpoints/getTable';
import { useUnsavedChanges } from '../../../../hooks/useUnsavedChanges';
import ConfirmationModal from '../../../../components/common/ConfirmationModal';

interface EditTableItemProps {
  // The record to edit, fetched and validated by the caller; absent on create.
  record?: FetchedTable | null;
  isNew?: boolean;
  handleClose: () => void;
  // Workspace create pre-target: seeds the zone + folder on create.
  createIn?: CreateTarget;
}

interface Column {
  name: string;
  type: string;
}

const EditTableItem: React.FC<EditTableItemProps> = ({ record, isNew = false, handleClose, createIn }) => {
  const dispatch = useAppDispatch();
  const currentTable = record ?? null;
  const { zones } = useAppSelector((state) => state?.listOfZones);

  const [tableName, setTableName] = useState<string>(currentTable?.table_name || '');
  const [columns, setColumns] = useState<Column[]>(
    // TODO #757 step 3 PR D: tighten table_columns type in tablesSlice
    (currentTable?.table_columns as Column[]) || [{ name: '', type: 'text' }]
  );
  const [zoneName, setZoneName] = useState<string>(currentTable?.zone_name || createIn?.zoneName || '');
  const [folderId, setFolderId] = useState<number | null>(currentTable?.folder_id ?? createIn?.folderId ?? null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);

  const currentFormValues = useMemo(() => ({ tableName, columns, zoneName, folderId }), [tableName, columns, zoneName, folderId]);
  const isDirtyRaw = useUnsavedChanges(true, currentFormValues);
  const isDirty = !isNew && isDirtyRaw;

  const handleRequestClose = () => {
    if (isDirty) {
      setShowCloseConfirm(true);
    } else {
      handleClose();
    }
  };

  useEffect(() => {
    dispatch(getAuthorizedZones({}) as any);
  }, [dispatch]);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    // The record being edited, or null on create. An edit is only ever opened on a
    // record (showEditTable fetches it first), so the early return never fires.
    const editing = isNew ? null : currentTable;
    if (!isNew && !editing) return;

    setIsSubmitting(true);

    const updatedTable = {
      table_name: tableName,
      table_columns: columns,
      table_privileges: {},
      zone_name: zoneName,
      folder_id: folderId,
    };

    let result;
    if (!editing) {
      result = await dispatch(createTable(updatedTable) as any);
    } else {
      result = await dispatch(
        updateTable({
          table_id: editing.table_id,
          original_zone_name: editing.zone_name,
          original_table_name: editing.table_name,
          ...updatedTable,
          zone_id: 0,
        }) as any
      );
    }

    setIsSubmitting(false);

    if (result.meta.requestStatus === 'rejected') {
      const payload = result.payload;
      const message = payload?.error || payload?.msg || 'Failed to save table';
      notify.error(message);
      return;
    }

    handleClose();
  };

  const handleColumnChange = (index: number, field: string, value: string) => {
    const updatedColumns = columns.map((col, i) => (i === index ? { ...col, [field]: value } : col));
    setColumns(updatedColumns);
  };

  const handleAddColumn = () => {
    setColumns([...columns, { name: '', type: 'text' }]);
  };

  const handleRemoveColumn = (index: number) => {
    const updatedColumns = columns.filter((_, i) => i !== index);
    setColumns(updatedColumns);
  };

  return (
    <Modal show={true} onHide={handleRequestClose} backdrop="static" keyboard={false} size="xl">
      <Modal.Header closeButton>
        <Modal.Title>{isNew ? 'New Table' : 'Edit Table'}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form onSubmit={handleSave}>
          <Form.Group as={Row} className="mb-3">
            <Form.Label column sm="2">
              Table Name
            </Form.Label>
            <Col sm="10">
              <Form.Control type="text" value={tableName} onChange={(e) => setTableName(e.target.value)} required />
            </Col>
          </Form.Group>

          <Form.Group as={Row} className="mb-3">
            <Form.Label column sm="2">
              Zone
            </Form.Label>
            <Col sm="10">
              <Form.Select
                aria-label="zone-select"
                value={zoneName}
                onChange={(e) => {
                  // Folder is zone-scoped — clear it when the zone changes.
                  setZoneName(e.target.value);
                  setFolderId(null);
                }}
                required
              >
                <option value="">Select a zone</option>
                {[...(zones || [])]
                  .sort((a: any, b: any) => a.name.localeCompare(b.name))
                  .map((zone: any) => (
                    <option key={zone.zone_id} value={zone.name}>
                      {zone.name}
                    </option>
                  ))}
              </Form.Select>
            </Col>
          </Form.Group>

          <Form.Group as={Row} className="mb-3">
            <Form.Label column sm="2">
              Folder
            </Form.Label>
            <Col sm="10">
              <FolderSelect value={folderId} onChange={setFolderId} zoneName={zoneName} />
            </Col>
          </Form.Group>

          <hr />

          <h5>Table Columns</h5>
          {columns.map((col, index) => (
            <Row key={index} className="align-items-center mb-2">
              <Col sm="5">
                <Form.Control
                  type="text"
                  placeholder="Column Name"
                  value={col.name}
                  onChange={(e) => handleColumnChange(index, 'name', e.target.value)}
                  required
                />
              </Col>
              <Col sm="4">
                <Form.Select value={col.type} onChange={(e) => handleColumnChange(index, 'type', e.target.value)}>
                  <option value="text">Text</option>
                  <option value="int">Integer</option>
                </Form.Select>
              </Col>
              <Col sm="3">
                <Button
                  variant="danger"
                  onClick={() => handleRemoveColumn(index)}
                  disabled={columns.length === 1} // Disable remove if only one column
                >
                  Remove
                </Button>
              </Col>
            </Row>
          ))}
          <Button variant="primary" onClick={handleAddColumn}>
            Add Column
          </Button>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleRequestClose}>
          Close
        </Button>
        <Button variant="primary" onClick={handleSave} disabled={isSubmitting}>
          {isSubmitting ? 'Saving...' : 'Save and Close'}
        </Button>
      </Modal.Footer>
      <ConfirmationModal
        show={showCloseConfirm}
        title="Unsaved Changes"
        body="You have unsaved changes. Are you sure you want to close without saving?"
        confirmText="Discard Changes"
        confirmVariant="danger"
        onConfirm={() => {
          setShowCloseConfirm(false);
          handleClose();
        }}
        onCancel={() => setShowCloseConfirm(false)}
      />
    </Modal>
  );
};

export default EditTableItem;
